import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import api, { apiErr, fmtTime, scriptCostSeconds } from "../../lib/api";
import { Card, btnPrimary, btnSecondary, Alert, StatusBadge, CostBadge } from "../ui-bits";
import { toast } from "sonner";
import { Plus, FolderOpen, Trash2, X, Film, Settings2, Clapperboard, Loader2, Wand2, PenLine, Clock, Sparkles, Lock, CalendarDays, Youtube, Eye, ThumbsUp, MessageCircle, TrendingUp, AlertTriangle, Smartphone, UserPlus } from "lucide-react";
import YoutubePublished from "./YoutubePublished";
import RecommendedVideos from "./RecommendedVideos";
import CalendarPanel from "./CalendarPanel";
import OneClickModal from "../OneClickModal";
import { useQuota } from "../../context/QuotaContext";
import { useUploads } from "../../context/UploadContext";

// 12345 -> "12.3K" (estadísticas reales de YouTube en la tarjeta de vídeo) — Intl nativo, sin
// dependencia nueva.
const fmtCompact = (n) => new Intl.NumberFormat(undefined, { notation: "compact" }).format(n || 0);

const GRID_PAGE_SIZE = 10;  // tarjetas que se añaden con cada "Cargar más"
const STALE_HOURS = 2;      // sin tocar un vídeo empezado, para considerarlo "a medias"
const HOT_STREAK_DAYS = 3;  // tras publicar, ventana en la que animamos a crear el siguiente
const INSIGHT_TONES = {
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  blue: "bg-blue-50 text-blue-800 border-blue-200",
  violet: "bg-violet-50 text-violet-800 border-violet-200",
};

function hoursSince(iso) {
  if (!iso) return null;
  const h = (Date.now() - new Date(iso).getTime()) / 3600000;
  return Number.isFinite(h) ? h : null;
}
function daysSince(iso) {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return Number.isFinite(d) ? d : null;
}

// Un proyecto puede haberse subido a YouTube más de una vez (p. ej. "Crear de nuevo" tras
// regenerar el B-roll de un vídeo ya publicado) — youtube_upload solo guarda el ÚLTIMO intento,
// así que si ESE se cancela/borra pero un intento ANTERIOR sigue vivo en YouTube, hay que
// mostrar ese anterior en vez de dar por "cancelado en YouTube" todo el proyecto.
function pickDisplayUpload(p, videoStats) {
  const candidates = [p.youtube_upload, ...((p.youtube_upload_history || []).slice().reverse())].filter(Boolean);
  for (const yt of candidates) {
    if (yt.status !== "done" || !yt.video_id) continue;
    const real = videoStats[yt.video_id];
    if (!real?.removed) return { yt, ytReal: real || null };
  }
  const latest = p.youtube_upload;
  return { yt: latest, ytReal: latest?.video_id ? (videoStats[latest.video_id] || null) : null };
}

// Orden de "Mis Vídeos" (3 grupos, cada uno con su propio criterio — pedido explícito del
// usuario, no es un único campo de fecha):
// 0) Sin publicar y sin programar (borrador, en curso, o subida cancelada/borrada en YouTube):
//    el trabajo pendiente sube arriba, ordenado por fecha de CREACIÓN — el más nuevo primero.
// 1) Programados en YouTube (aún no públicos): ordenados por su fecha de publicación
//    PROGRAMADA — los que saldrán más tarde en el tiempo, primero.
// 2) Ya publicados en YouTube: ordenados por la fecha REAL de publicación en YouTube
//    (snippet.publishedAt, no la programada — puede diferir si se publicó a mano) — el más
//    reciente primero.
function videoSortInfo(p, videoStats) {
  const { yt, ytReal } = pickDisplayUpload(p, videoStats);
  const ytRemoved = yt?.status === "done" && ytReal?.removed;
  const ytScheduled = yt?.status === "done" && !ytRemoved && (
    ytReal ? (ytReal.privacy_status === "private" && !!ytReal.publish_at)
           : (yt.publish_at && new Date(yt.publish_at) > new Date())
  );
  const ytPublished = yt?.status === "done" && !ytRemoved && !ytScheduled;
  if (ytPublished) {
    const publishedAt = ytReal?.published_at || yt.publish_at || yt.at;
    return { group: 2, key: publishedAt ? new Date(publishedAt).getTime() : 0 };
  }
  if (ytScheduled) {
    const publishAt = ytReal?.publish_at || yt.publish_at;
    return { group: 1, key: publishAt ? new Date(publishAt).getTime() : 0 };
  }
  return { group: 0, key: p.created_at ? new Date(p.created_at).getTime() : 0 };
}

// Posición de un short en la MISMA lista que los vídeos largos, con el mismo criterio de grupos
// que videoSortInfo: publicado en YouTube ordena por su fecha real de publicación, sin publicar
// por su fecha de creación. Los shorts no se pueden programar (se suben directos y públicos, ver
// la tarjeta de short más abajo), así que nunca caen en el grupo 1 (programados).
function shortSortInfo(sh, videoStats) {
  const yt = sh.youtube_upload;
  const real = yt?.video_id ? videoStats[yt.video_id] : null;
  const removed = yt?.status === "done" && real?.removed;
  if (yt?.status === "done" && !removed) {
    const publishedAt = real?.published_at || yt.at;
    return { group: 2, key: publishedAt ? new Date(publishedAt).getTime() : 0 };
  }
  return { group: 0, key: sh.created_at ? new Date(sh.created_at).getTime() : 0 };
}

// Un único aviso por canal (el más urgente primero), mismo criterio que en Mis Canales:
// 1) vídeo a medias abandonado > 2) acaba de publicar, sigue la ola > 3) nunca conectó YouTube
// > 4) nunca probó el calendario con IA.
function getChannelInsight(channel, videos, t) {
  const list = videos || [];
  const stalled = list.find((v) => {
    // Ya publicado en YouTube (p.ej. tras pulsar "Crear de nuevo" sobre un vídeo que ya
    // estaba en vivo): no está "abandonado", sigue habiendo un vídeo publicado disponible
    // mientras se revisa la nueva versión — no tiene sentido el aviso de urgencia.
    if (v.status === "Nuevo" || v.render?.status === "completo" || v.youtube_upload?.status === "done") return false;
    const h = hoursSince(v.updated_at);
    return h != null && h >= STALE_HOURS;
  });
  if (stalled) {
    return {
      tone: "amber", Icon: Clock, type: "video", projectId: stalled.id,
      text: t("videos.insightStale", { name: stalled.name || t("videos.untitled") }),
      cta: t("videos.insightStaleCta"),
    };
  }
  const mostRecent = list[0];
  const yt = mostRecent?.youtube_upload;
  if (mostRecent && yt?.status === "done" && !yt.publish_at) {
    const d = daysSince(yt.at);
    if (d != null && d <= HOT_STREAK_DAYS) {
      return { tone: "emerald", Icon: TrendingUp, type: "new", text: t("videos.insightHot"), cta: t("videos.insightHotCta") };
    }
  }
  if (list.length > 0 && !channel.youtube_connected) {
    return { tone: "blue", Icon: Youtube, type: "config", text: t("videos.insightConnectYt"), cta: t("videos.insightConnectYtCta") };
  }
  if (list.length >= 3 && !channel.has_calendar_entries) {
    return { tone: "violet", Icon: CalendarDays, type: "calendar", text: t("videos.insightTryCalendar"), cta: t("videos.insightTryCalendarCta") };
  }
  return null;
}

export default function TabVideos({ channel, videos, videosLoaded, loadVideos, openProject, updateChannel, autoOpenNew, onAutoOpenNewHandled }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { quota } = useQuota();
  const { startScript, startOneClick } = useUploads();
  const isFree = (quota?.plan || "gratis") === "gratis" && !quota?.has_spendable_credits;
  const maxMinutes = quota?.max_minutes || 40;
  const minMinutes = Math.min(10, maxMinutes);
  const [show, setShow] = useState(false);
  const [showOneClick, setShowOneClick] = useState(false);
  // Prefill del OneClickModal cuando se abre desde una idea IA o desde una entrada del calendario.
  // Estructura: { titulo, descripcion, sourceKind: "idea"|"calendar"|"manual", sourceRef, entryId }
  // - entryId (solo calendario): id de la entrada del calendario para relanzar `loadVideos` y que
  //   se refresque el chip en el calendario cuando el pipeline arranque.
  const [oneClickPrefill, setOneClickPrefill] = useState(null);
  const [name, setName] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [mode, setMode] = useState("ai");
  const [duration, setDuration] = useState(20);
  const [busy, setBusy] = useState(false);
  const formRef = useRef(null);
  const oneClickFormRef = useRef(null);
  const [section, setSection] = useState("videos"); // videos | calendario
  const [pendingEntry, setPendingEntry] = useState(null);
  const [pendingIdea, setPendingIdea] = useState(null);
  // Paginación "cargar más": la lista puede tener decenas de tarjetas, cada una con su
  // miniatura (o un <video> con preload="metadata" en los shorts) — pintarlas todas de golpe
  // dispara otras tantas peticiones de red. Se muestran de 10 en 10.
  const [visibleCount, setVisibleCount] = useState(GRID_PAGE_SIZE);
  // video_id -> {views, likes, comments, privacy_status, publish_at, removed}. "removed" y el
  // privacy_status/publish_at REALES son los que se usan para decidir el chip "Programado" /
  // "En YouTube" de cada tarjeta (ver más abajo) — el dato guardado en youtube_upload en el
  // momento de subir puede quedar obsoleto si el usuario cancela la programación o borra el
  // vídeo directamente desde YouTube Studio, sin pasar por YTMethod.
  const [videoStats, setVideoStats] = useState({});

  useEffect(() => {
    // Incluye también las subidas archivadas (youtube_upload_history): un proyecto puede
    // haberse subido más de una vez (p. ej. "Crear de nuevo") — hace falta saber si CUALQUIERA
    // de esos vídeos sigue vivo en YouTube, no solo el último intento.
    const ids = new Set();
    videos.forEach((p) => {
      if (p.youtube_upload?.status === "done" && p.youtube_upload?.video_id) ids.add(p.youtube_upload.video_id);
      (p.youtube_upload_history || []).forEach((h) => { if (h?.video_id) ids.add(h.video_id); });
      // Los shorts también pueden subirse a YouTube (ShortsCard) — mismo endpoint de stats,
      // así la tarjeta del short en la grid puede mostrar vistas/likes igual que un vídeo normal.
      (p.shorts || []).forEach((sh) => { if (sh.youtube_upload?.status === "done" && sh.youtube_upload?.video_id) ids.add(sh.youtube_upload.video_id); });
    });
    if (!ids.size) { setVideoStats({}); return; }
    const idList = [...ids];
    let cancel = false;
    api.get(`/channels/${channel.id}/youtube/video-stats`, { params: { video_ids: idList.join(",") } })
      .then(({ data }) => { if (!cancel) setVideoStats(data || {}); })
      .catch(() => { if (!cancel) setVideoStats({}); });
    return () => { cancel = true; };
  }, [videos, channel.id]);

  // Cada proyecto con shorts añade UNA tarjeta a la grid de "Mis Vídeos" (los shorts se ven aquí
  // como los vídeos normales, pero con formato propio — miniatura vertical 9:16 + insignia). Si
  // hay varios shorts del mismo proyecto solo se muestra el más reciente; el resto se resume con
  // un contador ("+N") en la propia tarjeta, en vez de llenar la grid de tarjetas repetidas.
  //
  // Vídeos y shorts se ordenan JUNTOS en una sola lista, cada uno por SU propia fecha (ago-2026,
  // cambio pedido explícitamente). Antes el short se colocaba pegado a su vídeo padre, así que su
  // posición la mandaba la fecha del padre: un short creado hoy aparecía abajo del todo si su
  // vídeo se había publicado hace meses, dando la sensación de que la lista no estaba ordenada.
  const gridItems = useMemo(() => {
    const items = [];
    videos.forEach((p) => {
      items.push({ kind: "video", key: p.id, project: p, sort: videoSortInfo(p, videoStats) });
      const shorts = [...(p.shorts || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      if (shorts.length > 0) {
        items.push({ kind: "short", key: `short-${shorts[0].id}`, project: p, short: shorts[0],
                     totalCount: shorts.length, sort: shortSortInfo(shorts[0], videoStats) });
      }
    });
    items.sort((a, b) => (a.sort.group !== b.sort.group ? a.sort.group - b.sort.group : b.sort.key - a.sort.key));
    return items;
  }, [videos, videoStats]);

  // Al cambiar de canal se vuelve a la primera página: sin esto, entrar en un canal con 3
  // vídeos después de haber pulsado "Cargar más" en otro dejaría el contador alto y daría la
  // falsa impresión de que ya está todo cargado.
  useEffect(() => { setVisibleCount(GRID_PAGE_SIZE); }, [channel.id]);

  const visibleItems = gridItems.slice(0, visibleCount);

  const openModal = () => {
    setSection("videos");
    setName(""); setDescripcion(""); setMode(isFree ? "empty" : "ai"); setDuration(Math.min(20, maxMinutes)); setShow(true); setPendingEntry(null); setPendingIdea(null);
    setShowOneClick(false); setOneClickPrefill(null); // paneles inline, mutuamente excluyentes
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };
  const openModalWith = (entry) => {
    const idea = entry?.idea || null;
    setSection("videos");
    setName(entry?.title || ""); setDescripcion(entry?.descripcion || ""); setMode(isFree ? "empty" : "ai");
    setDuration(idea?.duracion_min ? Math.min(maxMinutes, Math.max(minMinutes, idea.duracion_min)) : Math.min(20, maxMinutes));
    setShow(true); setPendingEntry(entry?.id || null); setPendingIdea(idea);
    setShowOneClick(false); setOneClickPrefill(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };
  // Abre el panel "IA · 1 clic" con datos prellenados desde una idea IA (angulo, patron_titulo)
  // o desde una entrada del calendario (title + idea si la tiene). El planos free se queda con el
  // form legacy porque el 1-clic solo tiene sentido en plan de pago.
  const openOneClickWith = (prefill) => {
    setSection("videos");
    setShow(false);
    setOneClickPrefill({
      titulo: prefill?.titulo || "",
      descripcion: prefill?.descripcion || "",
      sourceKind: prefill?.sourceKind || "manual",
      sourceRef: prefill?.sourceRef || "",
      entryId: prefill?.entryId || null,
    });
    setShowOneClick(true);
    setTimeout(() => oneClickFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };
  // Cuando se pulsa "Crear con IA" en una entrada del calendario: abre el 1-clic con
  // prellenado y linkado automático de la entrada. Si el plan es free, avisa (el 1-clic
  // requiere plan de pago porque genera guion + narración + render en un solo flujo).
  const openFromCalendarOneClick = (entry) => {
    if (isFree) {
      // Fallback razonable: sin plan de pago no puede usar 1-clic, así que caemos al form
      // legacy que ya sabe crear un vídeo vacío gratis.
      return openModalWith(entry);
    }
    const idea = entry?.idea || null;
    const descLines = [];
    if (idea?.angulo) descLines.push(idea.angulo);
    if (idea?.por_que_funciona) descLines.push(idea.por_que_funciona);
    // Una entrada creada a mano no tiene `idea` (eso solo lo rellena "Recomendados") — se cae
    // a la descripción libre que el usuario escribió al crear la entrada del calendario.
    openOneClickWith({
      titulo: entry?.title || "",
      descripcion: descLines.join("\n\n") || entry?.descripcion || "",
      sourceKind: "calendar",
      sourceRef: entry?.id || "",
      entryId: entry?.id || null,
    });
  };
  const closeForm = () => { setShow(false); setPendingEntry(null); setPendingIdea(null); };

  const linkEntry = async (projectId) => {
    if (!pendingEntry || !projectId) return;
    try { await api.patch(`/channels/${channel.id}/calendar/${pendingEntry}`, { project_id: projectId, status: "created" }); } catch (e) {}
  };

  // "Crear otro vídeo" desde la pantalla de éxito del 1-clic (ver Builder.jsx): al volver aquí
  // abre directamente el modal de nuevo vídeo, sin que el usuario tenga que pulsar otra vez.
  useEffect(() => {
    if (!autoOpenNew) return;
    setShow(false);
    setShowOneClick(true);
    setTimeout(() => oneClickFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    onAutoOpenNewHandled && onAutoOpenNewHandled();
  }, [autoOpenNew, onAutoOpenNewHandled]);

  // Antes solo cubria narration.status==generando y status=="Generando guion" — en cuanto
  // un video pasaba a Produciendo/Renderizando/En cola, el sondeo se apagaba y la tarjeta se
  // quedaba mostrando ese estado para siempre aunque el video ya hubiera terminado (bug real
  // reportado: "el video ha finalizado y sigue mostrando Render"). Ahora cubre cualquier
  // estado activo, igual que `producing` en el render de la tarjeta mas abajo.
  const anyGenerating = (videos || []).some((v) => v.narration?.status === "generando"
    || ["Generando guion", "Produciendo", "Renderizando", "En cola"].includes(v.status));
  useEffect(() => {
    if (!anyGenerating) return;
    const t2 = setInterval(() => loadVideos(), 6000);
    return () => clearInterval(t2);
  }, [anyGenerating, loadVideos]);

  const create = async () => {
    if (!name.trim()) { toast.warning(t("videos.warnTitle")); return; }
    setBusy(true);
    try {
      if (mode === "ai") {
        const aiBody = { ...(pendingIdea || {}), titulo: name.trim(), descripcion: descripcion.trim(), duracion_min: duration };
        const { data } = await api.post(`/channels/${channel.id}/generate-script`, aiBody);
        await linkEntry(data?.project_id);
        if (data?.project_id) startScript(data.project_id, name.trim());
        toast.success(t("videos.scriptStarted", { name: name.trim(), min: duration }));
        setShow(false); setName(""); setDescripcion(""); setPendingEntry(null); setPendingIdea(null);
        await loadVideos();
      } else {
        const { data } = await api.post("/projects", { name: name.trim(), channel_id: channel.id });
        await linkEntry(data?.id);
        toast.success(t("videos.created", { name: data.name }));
        setShow(false); setName(""); setPendingEntry(null); setPendingIdea(null);
        await loadVideos();
        openProject(data.id);
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(t("videos.confirmDelete"))) return;
    try { await api.delete(`/projects/${id}`); toast.success(t("videos.deleted")); loadVideos(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const channelInsight = getChannelInsight(channel, videos, t);

  // Próxima entrada del calendario aún sin vídeo creado — la usa el aviso "sigue la ola" para
  // abrir el 1-clic YA prellenado con esa idea en vez de una idea genérica en blanco.
  const [nextCalendarEntry, setNextCalendarEntry] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get(`/channels/${channel.id}/calendar`)
      .then(({ data }) => {
        if (cancel) return;
        const next = (data || []).find((e) => e.status !== "created" && !e.project_id);
        setNextCalendarEntry(next || null);
      })
      .catch(() => { if (!cancel) setNextCalendarEntry(null); });
    return () => { cancel = true; };
  }, [channel.id]);

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">{channel.name}</h1>
          {channel.description && (
            <p className="text-lg text-slate-600 mt-1 line-clamp-2 max-w-2xl" title={channel.description}>
              {channel.description}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-3">
          <button
            data-testid="oneclick-open-button"
            onClick={() => openOneClickWith({})}
            className="min-h-[48px] px-3 sm:px-5 text-base font-bold rounded-xl bg-gradient-to-br from-brand-400 to-brand-700 hover:brightness-90 text-white shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 whitespace-nowrap flex-1 sm:flex-none"
            title={t("videos.oneClickBtnTitle")}
          >
            <Wand2 size={20} className="shrink-0" />
            <span className="sm:hidden">{t("videos.oneClickOrigin")}</span>
            <span className="hidden sm:inline">{t("videos.oneClickBtn")}</span>
          </button>
          <button data-testid="new-video-button" onClick={openModal}
            className="shrink-0 min-h-[48px] px-3 sm:px-5 text-base font-bold rounded-xl bg-slate-100 text-slate-700 border-2 border-slate-200 hover:border-slate-400 transition-colors flex items-center justify-center gap-2 whitespace-nowrap flex-1 sm:flex-none">
            <Plus size={20} className="shrink-0" /> {t("videos.newVideo")}
          </button>
          <button data-testid="channel-config-btn" onClick={() => navigate(`/app/channels/${channel.id}/config`)} aria-label={t("videos.configChannel")}
            className="shrink-0 min-h-[48px] w-12 sm:w-auto sm:px-4 text-base font-semibold rounded-xl text-slate-500 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center gap-2">
            <Settings2 size={20} className="shrink-0" /> <span className="hidden sm:inline whitespace-nowrap">{t("videos.configChannel")}</span>
          </button>
        </div>
      </div>

      {channelInsight && (
        <button type="button" data-testid="channel-video-insight" onClick={() => {
          if (channelInsight.type === "video") openProject(channelInsight.projectId);
          else if (channelInsight.type === "new") {
            if (isFree) { openModal(); }
            else if (nextCalendarEntry) openFromCalendarOneClick(nextCalendarEntry);
            else openOneClickWith({});
          }
          else if (channelInsight.type === "config") navigate(`/app/channels/${channel.id}/config`);
          else if (channelInsight.type === "calendar") { setShow(false); setSection("calendario"); }
        }} className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${INSIGHT_TONES[channelInsight.tone]}`}>
          <channelInsight.Icon size={19} className="shrink-0" />
          <span className="flex-1 text-sm font-semibold">{channelInsight.text}</span>
          <span className="hidden sm:inline text-xs font-bold underline underline-offset-2 shrink-0">{channelInsight.cta}</span>
        </button>
      )}

      {/* Conmutador de sección: Vídeos / Calendario / Recomendados IA / Publicados en YouTube */}
      <div className="flex gap-2 border-b-2 border-slate-200 overflow-x-auto overflow-y-hidden vb-scroll">
        <button data-testid="section-videos" onClick={() => setSection("videos")} className={`px-4 py-2.5 text-lg font-bold border-b-[3px] -mb-[2px] transition-colors flex items-center gap-2 whitespace-nowrap ${section === "videos" ? "border-brand-600 text-brand-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}><Clapperboard size={20} /> {t("videos.tabVideos")}</button>
        <button data-testid="section-calendario" onClick={() => { setShow(false); setSection("calendario"); }} className={`px-4 py-2.5 text-lg font-bold border-b-[3px] -mb-[2px] transition-colors flex items-center gap-2 whitespace-nowrap ${section === "calendario" ? "border-brand-600 text-brand-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}><CalendarDays size={20} /> {t("videos.tabCalendar")}</button>
        <button data-testid="section-recomendados" onClick={() => { setShow(false); setSection("recomendados"); }} className={`px-4 py-2.5 text-lg font-bold border-b-[3px] -mb-[2px] transition-colors flex items-center gap-2 whitespace-nowrap ${section === "recomendados" ? "border-brand-600 text-brand-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}><Sparkles size={20} /> {t("videos.tabRecommended")}</button>
        <button data-testid="section-youtube" onClick={() => { setShow(false); setSection("youtube"); }} className={`px-4 py-2.5 text-lg font-bold border-b-[3px] -mb-[2px] transition-colors flex items-center gap-2 whitespace-nowrap ${section === "youtube" ? "border-brand-600 text-brand-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}><Youtube size={20} /> {t("videos.tabYoutube")}</button>
      </div>

      {section === "calendario" && (
        <CalendarPanel channel={channel} onCreateFromEntry={openModalWith} onCreateOneClick={openFromCalendarOneClick} onOpenProject={openProject} />
      )}

      {section === "recomendados" && (
        <RecommendedVideos channel={channel} openProject={openProject} loadVideos={loadVideos} onOpenConfig={() => navigate(`/app/channels/${channel.id}/config`)} onRecommendations={(ideas) => updateChannel && updateChannel({ recommendations: ideas })} onOneClick={(idea) => {
          const descLines = [];
          if (idea?.angulo) descLines.push(idea.angulo);
          if (idea?.por_que_funciona) descLines.push(idea.por_que_funciona);
          openOneClickWith({
            titulo: idea?.titulo || "",
            descripcion: descLines.join("\n\n"),
            sourceKind: "idea",
            sourceRef: idea?.id || idea?.titulo || "",
          });
        }} />
      )}

      {section === "youtube" && (
        <YoutubePublished channel={channel} />
      )}

      {section === "videos" && (<>

      {showOneClick && (
        <OneClickModal
          ref={oneClickFormRef}
          channelId={channel.id}
          channel={channel}
          sourceKind={oneClickPrefill?.sourceKind || "manual"}
          sourceRef={oneClickPrefill?.sourceRef || ""}
          initialTitle={oneClickPrefill?.titulo || ""}
          initialDescription={oneClickPrefill?.descripcion || ""}
          onClose={() => { setShowOneClick(false); setOneClickPrefill(null); }}
          onCreated={(pid, titulo) => {
            setShowOneClick(false);
            setOneClickPrefill(null);
            loadVideos && loadVideos();
            startOneClick(pid, titulo);
            openProject && openProject(pid);
          }}
        />
      )}

      {show && (
        <Card ref={formRef} data-testid="new-video-panel" className="p-6 sm:p-8 border-blue-200 scroll-mt-24">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-slate-900">{t("videos.newVideo")}</h3>
            <button data-testid="new-video-close" onClick={closeForm} className="text-slate-400 hover:text-slate-700"><X size={26} /></button>
          </div>
          <div className="space-y-5">
            <div>
              <label className="block text-base font-semibold text-slate-800 mb-2">{t("videos.titleLabel")}</label>
              <input data-testid="video-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("videos.titlePlaceholder")} className="w-full border-2 border-slate-300 rounded-lg px-4 py-3 text-lg focus:border-blue-600 focus:ring-4 focus:ring-blue-600/20 outline-none" />
            </div>

            <div>
              <label className="block text-base font-semibold text-slate-800 mb-2">{t("videos.howStart")}</label>
              <div className="grid grid-cols-1 gap-3">
                <div data-testid="mode-ai-card"
                  className={`rounded-xl border-2 transition-colors ${mode === "ai" ? "border-blue-600 bg-blue-50" : "border-slate-300"}`}>
                  <button data-testid="mode-ai-btn" onClick={() => setMode("ai")}
                    className="w-full text-left px-4 py-3 flex items-start gap-3">
                    <Wand2 size={22} className={mode === "ai" ? "text-blue-600 mt-0.5" : "text-slate-400 mt-0.5"} />
                    <div className="flex-1">
                      <div className="text-base font-bold text-slate-900 flex items-center gap-2">{t("videos.modeAi")} {isFree && <span className="inline-flex items-center gap-1 text-xs font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full"><Lock size={12} /> {t("videos.paidPlan")}</span>}</div>
                      <div className="text-sm text-slate-500">{t("videos.modeAiDesc")}</div>
                    </div>
                  </button>
                  {mode === "ai" && (
                    <div className="px-4 pb-4 pt-1 border-t border-blue-200/70 mt-1" data-testid="video-duration-control">
                      {pendingIdea && (pendingIdea.patron_titulo || pendingIdea.angulo || pendingIdea.por_que_funciona) && (
                        <div className="mt-3 space-y-2.5" data-testid="entry-idea-detail">
                          {pendingIdea.patron_titulo && (
                            <div className="text-sm text-blue-700 bg-blue-50 rounded-lg px-3 py-2 flex items-start gap-2">
                              <Sparkles size={15} className="mt-0.5 shrink-0" /> <span><b>{t("videos.suggestedTitle")}</b> {pendingIdea.patron_titulo}</span>
                            </div>
                          )}
                          {pendingIdea.angulo && (
                            <div>
                              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-0.5">{t("videos.angle")}</div>
                              <p className="text-sm text-slate-700">{pendingIdea.angulo}</p>
                            </div>
                          )}
                          {pendingIdea.por_que_funciona && (
                            <div className="text-sm text-emerald-800 bg-emerald-50 rounded-lg px-3 py-2">
                              <b>{t("videos.whyWorks")}</b> {pendingIdea.por_que_funciona}
                            </div>
                          )}
                        </div>
                      )}
                      {!pendingIdea && (
                        <div className="mt-3">
                          <label className="block text-base font-bold text-slate-800 mb-2">{t("videos.descLabel")} <span className="font-normal text-slate-400 text-sm">{t("videos.optional")}</span></label>
                          <textarea data-testid="video-description-input" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={3}
                            placeholder={t("videos.descPlaceholder")}
                            className="w-full border-2 border-slate-300 rounded-lg px-4 py-3 text-base focus:border-blue-600 focus:ring-4 focus:ring-blue-600/20 outline-none resize-none" />
                          <p className="text-xs text-slate-500 mt-1">{t("videos.descHint")}</p>
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-2 mt-4">
                        <label className="text-base font-bold text-slate-800">{t("videos.durationLabel")}</label>
                        <span className="text-lg font-bold text-blue-700" data-testid="video-duration-value">{duration} min</span>
                      </div>
                      <input data-testid="video-duration-slider" type="range" min={minMinutes} max={maxMinutes} step="1" value={duration}
                        onChange={(e) => setDuration(parseInt(e.target.value, 10))} className="w-full h-3 accent-blue-600 cursor-pointer" />
                      <div className="flex justify-between text-xs text-slate-400 mt-1"><span>{minMinutes} min</span><span>{maxMinutes} min</span></div>
                      <p className="text-sm text-slate-600 mt-3 flex items-center gap-1.5">
                        <Clock size={15} className="text-blue-600" />
                        {t("videos.consumes")} <b className="text-blue-700">{fmtTime(scriptCostSeconds(duration))}</b> {t("videos.consumesSuffix")}
                      </p>
                    </div>
                  )}
                </div>
                <button data-testid="mode-empty-btn" onClick={() => setMode("empty")}
                  className={`text-left rounded-xl border-2 px-4 py-3 flex items-start gap-3 transition-colors ${mode === "empty" ? "border-blue-600 bg-blue-50" : "border-slate-300 hover:border-slate-400"}`}>
                  <PenLine size={22} className={mode === "empty" ? "text-blue-600 mt-0.5" : "text-slate-400 mt-0.5"} />
                  <div>
                    <div className="text-base font-bold text-slate-900">{t("videos.modeEmpty")}</div>
                    <div className="text-sm text-slate-500">{t("videos.modeEmptyDesc")}</div>
                  </div>
                </button>
              </div>
            </div>

            <p className="text-sm text-slate-500"><Trans i18nKey="videos.willCreateIn" values={{ channel: channel.name }} components={{ 0: <b /> }} /></p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button data-testid="create-video-confirm" onClick={create} disabled={busy} className={`flex-1 ${btnPrimary}`}>
                {busy ? <Loader2 className="animate-spin" size={20} /> : (mode === "ai" ? <Sparkles size={20} /> : <Plus size={20} />)}
                {busy ? (mode === "ai" ? t("videos.generating") : t("videos.creating")) : (mode === "ai" ? t("videos.generateScript", { min: duration }) : t("videos.createVideo"))}
                {!busy && mode === "ai" && <CostBadge seconds={scriptCostSeconds(duration)} tip={t("videos.costTip")} />}
              </button>
              <button data-testid="new-video-cancel" onClick={closeForm} className="sm:w-auto px-4 sm:px-5 min-h-[48px] text-base font-bold rounded-xl bg-white text-slate-600 border-2 border-slate-300 hover:border-slate-400">{t("videos.cancel")}</button>
            </div>
          </div>
        </Card>
      )}

      {!videosLoaded ? (
        <Card className="p-10 text-center" data-testid="videos-loading">
          <Loader2 size={40} className="mx-auto text-blue-600 mb-3 animate-spin" />
          <p className="text-lg text-slate-500">{t("videos.loading")}</p>
        </Card>
      ) : videos.length === 0 ? (
        <Card className="p-10 text-center">
          <Film size={56} className="mx-auto text-blue-600 mb-4" strokeWidth={2} />
          <h2 className="text-2xl font-semibold text-slate-800 mb-2">{t("videos.emptyTitle")}</h2>
          <p className="text-lg text-slate-600 mb-6">{t("videos.emptyDesc")}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              data-testid="oneclick-first-video-button"
              onClick={() => setShowOneClick(true)}
              className="min-h-[48px] px-5 text-base font-bold rounded-xl bg-gradient-to-br from-brand-400 to-brand-700 hover:brightness-90 text-white shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <Wand2 size={22} /> {t("videos.createFirstOneClick")}
            </button>
            <button data-testid="new-first-video-button" onClick={openModal} className={`${btnSecondary}`}><Plus size={24} /> {t("videos.createFirst")}</button>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5" data-testid="videos-grid">
          {visibleItems.map((item) => {
            if (item.kind === "short") {
              const p = item.project;
              const sh = item.short;
              const shYt = sh.youtube_upload;
              // Mismo criterio que la tarjeta del vídeo largo (ver pickDisplayUpload/videoStats
              // más arriba): el estado REAL en YouTube (visto/borrado) manda sobre lo que
              // YTMethod guardó al subir. Los shorts no tienen programación (se suben directos
              // y públicos), así que solo hay dos estados posibles además de "sin subir": subido
              // o subiendo.
              const shStats = shYt?.video_id ? videoStats[shYt.video_id] : null;
              const shRemoved = shYt?.status === "done" && shStats?.removed;
              const shPublished = shYt?.status === "done" && !shRemoved;
              const shUploading = shYt?.status === "uploading";
              return (
                <Card key={item.key} data-testid={`short-card-${sh.id}`} onClick={() => openProject(p.id)}
                      className="p-0 overflow-hidden transition-all duration-200 group cursor-pointer hover:border-blue-300 hover:shadow-md">
                  {/* Mismo alto que la tarjeta del vídeo largo (aspect-video, 16:9). El short en
                      sí es vertical, así que object-cover recorta arriba/abajo para rellenar el
                      hueco entero — sin barras negras laterales, igual que las miniaturas de
                      vídeo normales. */}
                  <div className="relative aspect-video bg-slate-900">
                    {sh.video_url ? (
                      <video src={sh.video_url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-slate-500">
                        <Smartphone size={36} strokeWidth={1.5} />
                        <span className="text-xs font-medium mt-2">{t("videos.shortNoPreview")}</span>
                      </div>
                    )}
                    <div className="absolute top-2 left-2 flex flex-col items-start gap-1.5">
                      <span title={t("videos.shortBadgeTip")} className="flex items-center gap-1 bg-violet-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow">
                        <Smartphone size={13} /> {t("videos.shortBadge")}
                      </span>
                      {shRemoved && (
                        <span title={t("videos.ytRemovedTip")} className="flex items-center gap-1 text-white text-xs font-bold px-2 py-1 rounded-full shadow bg-amber-600">
                          <AlertTriangle size={13} /> {t("videos.ytRemoved")}
                        </span>
                      )}
                      {(shPublished || shUploading) && (
                        <span className={`flex items-center gap-1 text-white text-xs font-bold px-2 py-1 rounded-full shadow ${shPublished ? "bg-red-600" : "bg-slate-700"}`}>
                          {shUploading ? <Loader2 size={13} className="animate-spin" /> : <Youtube size={13} />}
                          {shUploading ? t("videos.ytUploading")
                            : `${t("videos.ytPublished")}${(shStats?.published_at || shYt.at) ? ` · ${new Date(shStats?.published_at || shYt.at).toLocaleDateString(undefined, { dateStyle: "short" })}` : ""}`}
                        </span>
                      )}
                    </div>
                    {item.totalCount > 1 && (
                      <span title={t("videos.shortCountTip", { n: item.totalCount })} className="absolute top-2 right-2 bg-slate-900/80 text-white text-xs font-bold px-2 py-1 rounded-full shadow">
                        +{item.totalCount - 1}
                      </span>
                    )}
                    {sh.duration != null && (
                      <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs font-semibold px-1.5 py-0.5 rounded">{fmtTime(sh.duration)}</span>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="text-lg font-semibold text-slate-900 truncate">{p.name}</div>
                    {/* Mismo badge (pill) que ShortsCard en Publicar/vídeo-listo, para que el
                        estado de publicación del short se lea igual en los dos sitios. El
                        badge superpuesto de arriba ya cubre publicado/subiendo/borrado — este
                        solo cubre el caso que faltaba ahí: aún sin subir. */}
                    {!shPublished && !shUploading && !shRemoved && (
                      <span data-testid={`short-status-${sh.id}`}
                            className="mt-2 inline-flex w-fit items-center px-2 py-0.5 rounded-full border-2 text-[11px] font-bold bg-slate-50 text-slate-500 border-slate-200">
                        {t("videos.shortNotPublished")}
                      </span>
                    )}
                    {shPublished && shStats && (
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-slate-500" data-testid={`short-stats-${sh.id}`}>
                        <span className="flex items-center gap-1"><Eye size={13} /> {fmtCompact(shStats.views)}</span>
                        <span className="flex items-center gap-1"><ThumbsUp size={13} /> {fmtCompact(shStats.likes)}</span>
                        <span className="flex items-center gap-1"><MessageCircle size={13} /> {fmtCompact(shStats.comments)}</span>
                        {shStats.subscribers_gained != null && (
                          <span title={t("videos.subsGainedTip")} className="flex items-center gap-1 text-emerald-600">
                            <UserPlus size={13} /> {fmtCompact(shStats.subscribers_gained)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              );
            }
            const p = item.project;
            const out = p.render?.output;
            const rendered = p.render?.status === "completo" && out?.thumbnail;
            // Estado real desde YouTube (si ya ha llegado la llamada batched de más arriba) —
            // gana siempre al dato guardado localmente al subir, que puede haber quedado
            // obsoleto (programación cancelada o vídeo borrado directamente en YouTube Studio).
            // pickDisplayUpload además prefiere un intento ANTERIOR que siga vivo si el último
            // se canceló/borró (ver comentario de la función).
            const { yt, ytReal } = pickDisplayUpload(p, videoStats);
            const ytRemoved = yt?.status === "done" && ytReal?.removed;
            const ytScheduled = yt?.status === "done" && !ytRemoved && (
              ytReal ? (ytReal.privacy_status === "private" && !!ytReal.publish_at)
                     : (yt.publish_at && new Date(yt.publish_at) > new Date())
            );
            const ytPublished = yt?.status === "done" && !ytRemoved && !ytScheduled;
            const ytUploading = yt?.status === "uploading";
            // Si el vídeo YA está publicado en YouTube, se enseña su miniatura real (la que de
            // verdad ve la audiencia, pudo cambiarse a mano en YouTube Studio) por delante de
            // todo lo demás. Si no está publicado, se mantiene el criterio de siempre:
            // custom_thumbnail (elegida/generada con IA) > frame automático del render.
            const cardThumb = (ytPublished && ytReal?.thumbnail) || p.custom_thumbnail?.url || (rendered ? out.thumbnail : null);
            const generating = p.status === "Generando guion";
            const generatingAudio = p.narration?.status === "generando";
            const failed = p.status === "Error" && p.script_gen?.error;
            // Mientras se produce (voz, material, montaje, render...) todavía no hay thumbnail,
            // así que sin esto la tarjeta enseñaba la MISMA claqueta estática que un proyecto
            // recién creado y vacío — no se distinguía "está trabajando" de "no hay nada aún".
            // Reusa las mismas etiquetas de paso (oc.step/oc.stepHint) que ya pinta el progreso
            // global del 1-clic, así el mensaje es el paso real, no un texto inventado aparte.
            const producing = !cardThumb && !generating && !failed
              && (generatingAudio || ["Produciendo", "Renderizando", "En cola"].includes(p.status));
            const ocStep = p.one_click?.step;
            const producingLabel = generatingAudio ? t("status.Generando audio") : p.one_click?.step_detail
              || (ocStep && t(`oc.stepHint.${ocStep}`, { defaultValue: t(`oc.step.${ocStep}`, { defaultValue: null }) }))
              || t(`status.${p.status}`, { defaultValue: p.status });
            // "Esperando revisión" NO es una IA trabajando, es un alto a propósito esperando que
            // el usuario apruebe algo — un spinner ahí mentiría (parecería que avanza solo), así
            // que lleva su propio tratamiento: icono fijo, sin girar, invitando a entrar.
            const needsReview = !cardThumb && !generating && !generatingAudio && !failed && p.status === "Esperando revisión";
            return (
              <Card key={p.id} data-testid={`video-card-${p.id}`} onClick={() => !generating && openProject(p.id)} className={`p-0 overflow-hidden transition-all duration-200 group ${generating ? "cursor-wait opacity-90" : "cursor-pointer hover:border-blue-300 hover:shadow-md"}`}>
                <div className="relative aspect-video bg-slate-100">
                  {generating ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 text-blue-600">
                      <Loader2 size={40} className="animate-spin" />
                      <span className="text-sm font-semibold mt-2">{t("videos.generatingScript")}</span>
                      {p.script_gen?.words > 0 && <span className="text-xs text-blue-500 mt-0.5">{t("videos.words", { n: p.script_gen.words })}</span>}
                    </div>
                  ) : failed ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 text-red-600 px-3 text-center">
                      <Clapperboard size={36} strokeWidth={1.5} />
                      <span className="text-xs font-medium mt-2">{p.script_gen.error}</span>
                    </div>
                  ) : cardThumb ? (
                    <img src={cardThumb} alt={p.name} className="w-full h-full object-cover" />
                  ) : producing ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-violet-50 to-indigo-100 text-violet-600 px-3 text-center">
                      <Loader2 size={36} className="animate-spin" />
                      <span className="text-sm font-semibold mt-2">{producingLabel}</span>
                    </div>
                  ) : needsReview ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-sky-50 to-blue-100 text-sky-600 px-3 text-center">
                      <Eye size={36} strokeWidth={1.5} />
                      <span className="text-sm font-semibold mt-2">{t("oc.step.awaiting_approval")}</span>
                    </div>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 text-slate-400">
                      <Clapperboard size={42} strokeWidth={1.5} />
                      <span className="text-sm font-medium mt-2">{t("videos.noVideoYet")}</span>
                    </div>
                  )}
                  {rendered && out?.duration != null && (
                    <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs font-semibold px-1.5 py-0.5 rounded">{fmtTime(out.duration)}</span>
                  )}
                  {ytRemoved && (
                    <span data-testid={`video-yt-removed-${p.id}`} title={t("videos.ytRemovedTip")} className="absolute top-2 left-2 flex items-center gap-1 text-white text-xs font-bold px-2 py-1 rounded-full shadow bg-amber-600">
                      <AlertTriangle size={13} /> {t("videos.ytRemoved")}
                    </span>
                  )}
                  {(ytPublished || ytScheduled || ytUploading) && (
                    <span className={`absolute top-2 left-2 flex items-center gap-1 text-white text-xs font-bold px-2 py-1 rounded-full shadow ${ytPublished ? "bg-red-600" : ytScheduled ? "bg-indigo-600" : "bg-slate-700"}`}>
                      {ytUploading ? <Loader2 size={13} className="animate-spin" /> : <Youtube size={13} />}
                      {ytUploading ? t("videos.ytUploading") : ytScheduled
                        ? `${t("videos.ytScheduled")} · ${new Date(ytReal?.publish_at || yt.publish_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}`
                        : (() => {
                            // Fecha en que el vídeo se hizo público DE VERDAD. ytReal.published_at
                            // (snippet.publishedAt de la API de YouTube) es la fuente fiable — se
                            // actualiza sola si el vídeo se publicó antes o después de lo programado
                            // (p. ej. publicado a mano desde YouTube Studio). yt.publish_at es solo
                            // lo que YTMethod guardó como fecha PROGRAMADA al subir: si el vídeo
                            // se publicó en otro momento, ese dato queda obsoleto (bug real
                            // detectado ago-2026 — la tarjeta seguía mostrando la programación
                            // vieja). yt.at es el último respaldo, cuándo YTMethod terminó de
                            // SUBIRLO (anterior a la publicación real en una subida programada).
                            const publicAt = ytReal?.published_at || yt.publish_at || yt.at;
                            return `${t("videos.ytPublished")}${publicAt ? ` · ${new Date(publicAt).toLocaleDateString(undefined, { dateStyle: "short" })}` : ""}`;
                          })()}
                    </span>
                  )}
                  {!generating && (
                    <button data-testid={`delete-video-${p.id}`} onClick={(e) => remove(p.id, e)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 hover:bg-white text-slate-500 hover:text-red-600 rounded-full p-1.5"><Trash2 size={18} /></button>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-lg font-semibold text-slate-900 truncate">{p.name}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <StatusBadge status={generatingAudio ? "Generando audio" : p.status} busy={generatingAudio} />
                    {p.one_click && (
                      <span title={t("videos.oneClickOriginTip")} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-2 border-amber-300 bg-amber-50 text-amber-700 text-xs font-bold">
                        <Wand2 size={11} /> {t("videos.oneClickOrigin")}
                      </span>
                    )}
                  </div>
                  {/* flex-wrap (bug real reportado): la tarjeta tiene overflow-hidden (recorta la
                      miniatura a las esquinas redondeadas) — sin wrap, un vídeo con estadísticas
                      muy altas (vistas/likes en cientos de miles) alarga esta fila lo suficiente
                      para que el propio overflow-hidden se coma el último badge en vez de bajarlo
                      a una segunda línea. */}
                  {ytPublished && videoStats[yt.video_id] && (
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-slate-500" data-testid={`video-stats-${p.id}`}>
                      <span className="flex items-center gap-1"><Eye size={13} /> {fmtCompact(videoStats[yt.video_id].views)}</span>
                      <span className="flex items-center gap-1"><ThumbsUp size={13} /> {fmtCompact(videoStats[yt.video_id].likes)}</span>
                      <span className="flex items-center gap-1"><MessageCircle size={13} /> {fmtCompact(videoStats[yt.video_id].comments)}</span>
                      {/* Suscriptores ganados con ESTE vídeo (YouTube Analytics API,
                          subscribersGained por video — ver _yt_video_subs_gained en el backend).
                          Aparte de vistas/likes/comentarios (Data API): puede faltar si el canal
                          se conectó antes de añadir el scope yt-analytics.readonly, así que solo
                          se muestra cuando el dato realmente llegó. */}
                      {videoStats[yt.video_id].subscribers_gained != null && (
                        <span title={t("videos.subsGainedTip")} className="flex items-center gap-1 text-emerald-600">
                          <UserPlus size={13} /> {fmtCompact(videoStats[yt.video_id].subscribers_gained)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
          </div>
          {gridItems.length > visibleCount && (
            <div className="flex justify-center pt-2">
              <button data-testid="videos-load-more" onClick={() => setVisibleCount((n) => n + GRID_PAGE_SIZE)}
                className="inline-flex items-center justify-center gap-2 min-h-[48px] px-6 text-base font-bold rounded-xl border-2 border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50 transition-colors">
                {t("videos.loadMore", { count: gridItems.length - visibleCount })}
              </button>
            </div>
          )}
        </div>
      )}

      </>)}
    </div>
  );
}
